import React, { useState } from 'react';
import { campaignAPI, Campaign } from '../services/api';
import Icon from './Icon';
import { useT } from '../i18n/LanguageContext';

interface TestSendBoxProps {
  campaign: Campaign;
}

/**
 * Send a single test message for a campaign to an ad-hoc recipient.
 * Channel is inferred from the campaign: Viber/SMS -> phone input, email -> email input.
 */
const TestSendBox: React.FC<TestSendBoxProps> = ({ campaign }) => {
  const t = useT();
  const isViber = campaign.channelId === 3 || !!campaign.viberRichContent;
  const isSms = campaign.channelId === 2;
  const isPhoneChannel = isViber || isSms;

  const [to, setTo] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; msg: string } | null>(null);

  const handleSend = async () => {
    if (!to.trim()) return;
    setSending(true);
    setResult(null);
    try {
      const res = isPhoneChannel
        ? await campaignAPI.sendTestViber(campaign.id, to.trim())
        : await campaignAPI.sendTestEmail(campaign.id, to.trim());
      setResult({ ok: true, msg: res.data.message || t('Test poslat.', 'Test sent.') });
    } catch (err: any) {
      setResult({ ok: false, msg: err.response?.data?.error || t('Slanje testa nije uspelo.', 'Failed to send test.') });
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="card card-pad" style={{ marginTop: 24 }}>
      <h3 className="card-title" style={{ marginTop: 0, marginBottom: 6 }}>
        <Icon name="send" size={16} /> {t('Pošalji test', 'Send Test')} {isPhoneChannel ? (isViber ? 'Viber' : 'SMS') : 'Email'}
      </h3>
      <p className="hint" style={{ marginTop: 0, marginBottom: 14 }}>
        {t('Šalje jednu poruku koristeći sadržaj ove kampanje i', "Sends one message using this campaign's content and the tenant's")}{' '}
        {isPhoneChannel ? 'NTH' : 'SES'} {t('kredencijale zakupca.', 'credentials.')}
        {/* NTH/SES is a dynamic identifier and stays verbatim */}
      </p>
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          className="input"
          type={isPhoneChannel ? 'tel' : 'email'}
          value={to}
          onChange={(e) => setTo(e.target.value)}
          placeholder={isPhoneChannel ? '+3816412345678' : 'test@example.com'}
          style={{ flex: 1, minWidth: 220 }}
        />
        <button
          className="btn btn-primary"
          onClick={handleSend}
          disabled={sending || !to.trim()}
        >
          {sending ? t('Slanje…', 'Sending…') : t('Pošalji test', 'Send Test')}
        </button>
      </div>
      {result && (
        <div
          style={{
            marginTop: 12,
            padding: 10,
            borderRadius: 5,
            fontSize: 14,
            backgroundColor: result.ok ? 'var(--surface-2)' : 'var(--danger-soft)',
            color: result.ok ? 'var(--brand-700)' : 'var(--danger)',
            border: `1px solid ${result.ok ? 'var(--ok)' : 'var(--danger)'}`,
          }}
        >
          {result.ok ? <Icon name="checkCircle" size={14} /> : <Icon name="x" size={14} />} {result.msg}
        </div>
      )}
    </div>
  );
};

export default TestSendBox;
